/**
 * Country data utilities
 * Uses the mledoze countries dataset to build continent and country lists.
 * Source: https://raw.githubusercontent.com/mledoze/countries/refs/heads/master/dist/countries.json
 */

/**
 * @typedef {{ code: string, name: string }} Option
 */

/** @type {string} */
export const COUNTRIES_DATA_URL = 'https://raw.githubusercontent.com/mledoze/countries/refs/heads/master/dist/countries.json';

/**
 * Cache for the parsed lists to avoid refetching
 * @type {{ continents: Option[], countriesByContinent: Record<string, Option[]> } | null}
 */
let cachedCountryLists = null;

/**
 * Fetch and build continent and country options from the dataset.
 * Continents are taken from the dataset's `region` field (e.g., "Africa", "Americas", "Asia", "Europe", "Oceania", "Antarctica").
 * Country codes use `cca2` and names use `name.common`.
 *
 * @returns {Promise<{ continents: Option[], countriesByContinent: Record<string, Option[]> }>} 
 */
export async function fetchCountryLists() {
  if (cachedCountryLists) return cachedCountryLists;

  const response = await fetch(COUNTRIES_DATA_URL, { cache: 'force-cache' });
  if (!response.ok) {
    throw new Error(`Failed to load countries.json: ${response.status}`);
  }
  /** @type {any[]} */
  const data = await response.json();

  /** @type {Record<string, Option[]>} */
  const countriesByContinent = {};

  for (const item of data) {
    const region = (item && item.region) || '';
    const countryCode = (item && item.cca2) || '';
    const countryName = item && item.name && item.name.common;
    if (!region || !countryCode || !countryName) continue;

    if (!countriesByContinent[region]) countriesByContinent[region] = [];
    countriesByContinent[region].push({ code: countryCode, name: countryName });
  }

  // Sort countries within each continent by name
  Object.values(countriesByContinent).forEach(list => list.sort((a, b) => a.name.localeCompare(b.name)));

  /** @type {Option[]} */
  const continents = Object.keys(countriesByContinent)
    .sort((a, b) => a.localeCompare(b))
    .map(name => ({ code: name, name }));

  cachedCountryLists = { continents, countriesByContinent };
  return cachedCountryLists;
}


